import { toast } from 'react-toastify';

export interface GraphQLError {
    message: string;
    extensions?: {
        message?: string;
        code?: string;
        [key: string]: any;
    };
    locations?: any[];
    path?: any[];
}

export interface ApolloError {
    message?: string;
    graphQLErrors?: GraphQLError[];
    networkError?: {
        message: string;
        statusCode?: number;
        [key: string]: any;
    };
}

/**
 * Extracts the most helpful error message from an Apollo/GraphQL error
 * Prioritizes user-friendly messages from extensions over technical messages
 */
export function extractErrorMessage(error: ApolloError): string {
    // Handle GraphQL errors first (these are our validation errors)
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        
        // Prioritize extensions.message (our user-friendly validation messages)
        if (graphQLError.extensions?.message) {
            return graphQLError.extensions.message;
        }
        
        // Fallback to the main GraphQL message
        if (graphQLError.message) {
            return graphQLError.message;
        }
    }
    
    // Handle network errors
    if (error.networkError) {
        if (error.networkError.statusCode === 401) {
            return "Authentication error: Please refresh the page to continue.";
        }
        return `Network error: ${error.networkError.message}`;
    }
    
    // Final fallback to general error message
    if (error.message) {
        return error.message;
    }
    
    return 'An unexpected error occurred';
}

/**
 * Shows an error toast with the most helpful message extracted from the error
 * This should be used for consistent error display across the app
 */
export function showErrorToast(error: ApolloError, customMessage?: string): void {
    const errorMessage = customMessage || extractErrorMessage(error);
    
    // Check if it's a cancellation (don't show toast for these)
    if (error.graphQLErrors?.some(err => 
        err.extensions?.code === "REQUEST_CANCELLED" || 
        err.message?.includes("cancelled") || 
        err.message?.includes("canceled")
    )) {
        console.info("Request was cancelled, not showing error toast");
        return;
    }
    
    // Check if it's an auth error (handle specially)
    if (error.graphQLErrors?.some(err => err.extensions?.code === "AUTH_NOT_AUTHENTICATED")) {
        toast.error("Your session has expired. Please refresh the page to continue.");
        return;
    }
    
    if (error.graphQLErrors?.some(err => err.extensions?.message === "Attempted to perform an unauthorized operation.")) {
        toast.error("Access Denied: You do not have permission to perform this operation.");
        return;
    }
    
    // Show the error toast
    toast.error(errorMessage);
}

/**
 * Shows a success toast (for consistency)
 */
export function showSuccessToast(message: string): void {
    toast.success(message);
}

/**
 * Shows an info toast (for consistency)
 */
export function showInfoToast(message: string): void {
    toast.info(message);
}

/**
 * Determines if an error should be handled silently (not shown to user)
 */
export function shouldHandleErrorSilently(error: ApolloError): boolean {
    // Client cancellations
    if (error.graphQLErrors?.some(err => 
        err.extensions?.code === "REQUEST_CANCELLED" || 
        err.message?.includes("cancelled") || 
        err.message?.includes("canceled")
    )) {
        return true;
    }
    
    // Network aborts
    if (error.networkError?.message?.includes('aborted') || 
        (error.networkError as any)?.name === 'AbortError') {
        return true;
    }
    
    return false;
}